import { OptionallyVisible, PageWrapper } from 'components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { ErrorText } from 'pages/error/styles';

import { selectAccountType } from 'state/slices/accountSlice';
import { selectUserLevel } from 'state/slices/userSlice';

import { AccountType } from 'types/account';
import { CryptoPayoutStatus } from 'types/cryptoPayout';

import { hasUserS4cRole } from 'utils/hasUserS4cRole';

import isFiniteCryptoPayoutStatus, { usePolledCryptoPayoutResponse } from '../utils';
import { CryptoDetailsSection } from './components/cryptoDetails';
import { CryptoPayoutCustomerSection } from './components/customer';
import { CryptoPayoutEventsSection } from './components/events';
import { CryptoPayoutExchangeSection } from './components/exchange';
import { CryptoPayoutDetailsOutlineSection } from './components/outline';
import CryptoPayoutDetailsSection from './components/payout';
import { BREADCRUMBS, LABELS } from './keys';
import { Container, Header, SubContainer } from './styles';

export const CryptoPayoutViewContainer = () => {
  const { uuid } = useParams();
  const { t } = useTranslation();
  const breadcrumbs = BREADCRUMBS.map((breadcrumb) => t(breadcrumb));
  const { payoutResponse, isLoading, error, refetch } = usePolledCryptoPayoutResponse(uuid!);
  const userLevel = useSelector(selectUserLevel);
  const accountType = useSelector(selectAccountType);
  const isS4cUser = hasUserS4cRole(userLevel);
  const showMerchantName = isS4cUser || accountType === AccountType.PSP || accountType === AccountType.Pwc_Group;

  const hasFiniteStatus = isFiniteCryptoPayoutStatus(payoutResponse?.data?.status ?? CryptoPayoutStatus.Initiated);
  const isExchangeSectionAvailable = hasFiniteStatus && isS4cUser;

  if (isLoading) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <LoadingIndicator padded />
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <ErrorText>{t(LABELS.LOADING_ERROR)}</ErrorText>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper breadcrumbs={breadcrumbs}>
      <Header>{t(LABELS.PAGE_TITLE)}</Header>
      <CryptoPayoutDetailsOutlineSection
        uuid={uuid!}
        payoutTotal={payoutResponse!.data!.fiatAmount}
        status={payoutResponse!.data!.status}
      />
      <Container>
        <SubContainer>
          <CryptoPayoutDetailsSection payout={payoutResponse!.data!} showMerchantName={showMerchantName} refetch={refetch} />
          <OptionallyVisible visible={hasFiniteStatus}>
            <CryptoDetailsSection payout={payoutResponse!.data!} />
          </OptionallyVisible>
        </SubContainer>

        <SubContainer>
          <CryptoPayoutCustomerSection payout={payoutResponse!.data!} />
          <CryptoPayoutEventsSection payout={payoutResponse!.data!} />
          <OptionallyVisible visible={isExchangeSectionAvailable}>
            <CryptoPayoutExchangeSection payout={payoutResponse!.data!} />
          </OptionallyVisible>
        </SubContainer>
      </Container>
    </PageWrapper>
  );
};

export default CryptoPayoutViewContainer;
